import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Grid,
  Text,
  useToast,
  FormControl,
  FormLabel,
  Flex,
  Input,
  Checkbox
} from "@chakra-ui/react";
import AccountApi from "api/account";
import AccountForm from "components/Sections/AccountForm";
import CodesSection from "components/Sections/CodesSection";
import AccountStatus from "components/Sections/AccountStatus";
import SalesHistoryModal from "./SalesHistoryModal";
import ProductApi from "../../api/product";

const EditAccountModal = ({ isOpen, onClose, accountId }) => {
  const [accountData, setAccountData] = useState({
    email: "",
    password: "",
    initial_price: "",
    is_deactive: "",
    product_id: "",
  });
  const [productSelect, setProductSelect] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [codes, setCodes] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [isNewCodeModalOpen, setIsNewCodeModalOpen] = useState(false);
  const [isSalesHistoryModalOpen, setIsSalesHistoryModalOpen] = useState(false);
  const [newCodes, setNewCodes] = useState("");
  const toast = useToast();

  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response = await AccountApi.Get(accountId);
        const { email, password, initial_price, is_deactive, codes, statuses, product } = response.data.data;
        setAccountData({
          email,
          password,
          initial_price,
          is_deactive: String(is_deactive),
          product_id: (product && product.id) ? product.id : "",
        });
        if (product && product.id) {
          setProductSelect({ value: product.id, label: product.name || `#${product.id}` });
        } else {
          setProductSelect(null);
        }
        setCodes(codes);
        setStatuses(statuses);
      } catch (error) {
        console.error("Failed to fetch account data", error);
      }
    };

    if (isOpen) {
      fetchAccountData();
    }
  }, [isOpen, accountId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const loadProductOptions = async (inputValue, callback) => {
    try {
      const res = await ProductApi.Products(inputValue || "", 1);
      const options = (res.data.data || []).map((p) => ({ value: p.id, label: p.name, full: p }));
      callback(options);
    } catch (e) {
      callback([]);
    }
  };

  const handleProductChange = (option) => {
    setProductSelect(option);
    setAccountData((prev) => ({
      ...prev,
      product_id: option ? option.value : "",
    }));
  };

  const handleDeactiveCheckboxChange = ({ name, value }) => {
    setAccountData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleCodeCheckboxChange = async (e) => {
    const codeId = parseInt(e.target.name);
    const isChecked = e.target.checked;

    const updatedCode = codes.find((code) => code.id === codeId);
    if (!updatedCode) return;

    setCodes((prevCodes) =>
      prevCodes.map((code) =>
        code.id === codeId ? { ...code, is_used: isChecked } : code
      )
    );

    try {
      await AccountApi.UpdateCode(codeId, { is_used: isChecked });
      toast({
        title: "موفقیت",
        description: "وضعیت کد با موفقیت به روزرسانی شد",
        status: "success",
        duration: 2000,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "خطا",
        description: "خطا در به روزرسانی وضعیت کد",
        status: "error",
        duration: 2000,
        position: "top-right",
      });
    }
  };

  const handlePasswordToggle = () => {
    setShowPassword(!showPassword);
  };

  const handleAccountSubmit = async () => {
    try {
      await AccountApi.Update(accountId, { ...accountData, statuses });
      toast({
        title: "موفقیت",
        description: "مشخصات اکانت با موفقیت به روزرسانی شد",
        status: "success",
        duration: 2500,
        position: 'top-right',
      });
    } catch (error) {
      if (error.response && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        console.error("Failed to update account data", error);
        toast({
          title: "خطا",
          description: "خطا در به روزرسانی اکانت",
          status: "error",
          duration: 2500,
          position: 'top-right',
        });
      }
    }
  };

  const handleImportCodesSubmit = async () => {
    try {
      const newCodesArray = newCodes
        .split('\n')
        .map(code => code.trim())
        .filter(code => code !== '');

      const response = await AccountApi.SaveCodes({ codes: newCodesArray, account_id: accountId });

      const newCodesFromResponse = response.data.codes;
      setCodes(newCodesFromResponse);

      toast({
        title: "موفقیت",
        description: "کدهای جدید با موفقیت اضافه شدند",
        status: "success",
        duration: 2500,
        position: 'top-right',
      });
      closeNewCodeModal();
    } catch (error) {
      if (error.response && error.response.data.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat().join('\n');
        toast({
          title: "خطا",
          description: errorMessages,
          status: "error",
          duration: 2500,
          position: 'top-right',
        });
      } else {
        toast({
          title: "خطا",
          description: "خطا در اضافه کردن کدها",
          status: "error",
          duration: 2500,
          position: 'top-right',
        });
      }
    }
  };

  const handleStatusInputChange = (statusId, e) => {
    const { name, value } = e.target;
    const updatedStatuses = statuses.map(status =>
      status.id === statusId ? { ...status, [name]: value } : status
    );
    setStatuses(updatedStatuses);
  };

  const handleStatusCheckboxChange = async (statusId, e) => {
    const { name, checked } = e.target;
    const status = statuses.find(status => status.id === statusId);
    if (!status) return;
  
    const worderId = checked ? status.worder_id : ''; // Clear worder_id if unchecked
  
    // Update state to reflect the change
    const updatedStatuses = statuses.map((status) =>
      status.id === statusId ? { ...status, [name]: checked ? 1 : 0, worder_id: worderId } : status
    );
    setStatuses(updatedStatuses);
  
    // Update the status on the server
    try {
      await AccountApi.UpdateStatus(statusId, {
        worder_id: worderId,
        is_full: checked ? 1 : 0,
      });
      toast({
        title: "موفقیت",
        description: "وضعیت به روزرسانی شد",
        status: "success",
        duration: 2000,
        position: "top-right",
      });
    } catch (error) {
      toast({
        title: "خطا",
        description: "خطا در به روزرسانی وضعیت",
        status: "error",
        duration: 2000,
        position: "top-right",
      });
    }
  };
  
  
  
  

  const openNewCodeModal = () => {
    setIsNewCodeModalOpen(true);
  };

  const closeNewCodeModal = () => {
    setIsNewCodeModalOpen(false);
    setNewCodes(""); 
  };

  const openSalesHistoryModal = () => setIsSalesHistoryModalOpen(true);
  const closeSalesHistoryModal = () => setIsSalesHistoryModalOpen(false);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent borderRadius="md" boxShadow="lg">
          <ModalHeader fontSize="lg" fontWeight="bold">
            ویرایش اکانت
          </ModalHeader>
          <ModalCloseButton position="absolute" left="9px" right="auto"/>
          <ModalBody>
          <Grid templateColumns="30% 35% 35%" gap={3}>
            <Box p={4}  borderColor="gray.200">
              <Text fontSize="lg" fontWeight="bold" mb={4}>
                ویرایش اکانت
              </Text>
              <AccountForm
                accountData={accountData}
                showPassword={showPassword}
                errors={errors}
                onInputChange={handleInputChange}
                onCheckboxChange={handleDeactiveCheckboxChange}
                onSubmit={handleAccountSubmit}
                onPasswordToggle={handlePasswordToggle}
                productSelect={productSelect}
                loadProductOptions={loadProductOptions}
                onProductChange={handleProductChange}
              />
            </Box>
            
            <Box p={4} borderRight="1px" borderColor="gray.200">
              <CodesSection
                codes={codes}
                onCheckboxChange={handleCodeCheckboxChange}
                onOpenNewCodeModal={openNewCodeModal}
              />
            </Box>
           
            <AccountStatus
              statuses={statuses}
              handleStatusInputChange={handleStatusInputChange}
              handleStatusCheckboxChange={handleStatusCheckboxChange}
            />
          </Grid>

          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Box>
              <Button colorScheme="orange" onClick={openSalesHistoryModal} borderRadius="md" size="sm">
                تاریخچه فروش
              </Button>
            </Box>
            
            <Button variant="ghost" onClick={onClose} borderRadius="md">
              بستن
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isNewCodeModalOpen} onClose={closeNewCodeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="lg" fontWeight="bold">
            وارد کردن کدهای جدید
          </ModalHeader>
          <ModalCloseButton position="absolute" left="8px" right="auto" />
          <ModalBody>
            <FormControl>
              <FormLabel>کدهای جدید (هر کد در یک خط)</FormLabel>
              <Input
                as="textarea"
                rows={10}
                height="auto"
                value={newCodes}
                onChange={(e) => setNewCodes(e.target.value)}
                placeholder="کدهای جدید را در اینجا وارد کنید"
                textAlign="left"
                padding="1rem"
                border="1px solid"
                borderColor="gray.300"
                boxShadow="sm"
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleImportCodesSubmit} borderRadius="md">
              ذخیره
            </Button>
            <Button variant="ghost" onClick={closeNewCodeModal} borderRadius="md" ml={3}>
              بستن
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <SalesHistoryModal isOpen={isSalesHistoryModalOpen} onClose={closeSalesHistoryModal} accountId={accountId} />
    </>
  );
};

export default EditAccountModal;
